/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Component imports
import { Button, Col, Collapse, Empty, Input, Row, Space, Spin, Tooltip, Typography } from 'antd';
import { ArrowLeftOutlined, PlusCircleFilled, SearchOutlined, StarFilled, StarOutlined, TeamOutlined } from '@ant-design/icons';
import AutomationsSidebarForm from '../Forms/AutomationsSidebarForm';
import PredefinedResponsesSidebarForm from '../Forms/PredefinedResponsesSidebarForm';
import { ArrowRightIcon } from '../Icons/ArrowRightIcon';

// Misc imports
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { updateFavoriteAutomations } from '../../store/liveChat/liveChatThunks';
import { uniqueBy } from '../../utils/array/uniqueBy';
import { STORAGE_KEYS } from '../../constants/main';
import generateGroupedEntities from '../../utils/misc/groupPredefinedEntitiesByCategory';
import extractContextAroundWord from '../../utils/string/extractContextAroundWord';

const PredefinedEntitiesPanel = ({
  onPredefinedEntityClick,
  isInactive = false,
  hasSearch = false,
  isEditMode = false,
  conversationId,
  predefinedEntity = '',
  onEntityTrigger = () => {},
  setIsSearchingResponses = () => {}
}) => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [organizationFavoriteAutomations, setOrganizationFavoriteAutomations] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [predefinedEntities, setPredefinedEntities] = useState([]);
  const { data: predefinedResponses } = getCurrentQueryState('fetchPredefinedResponses', { orgId, urlParams: { tenantScope: conversationId } }, predefinedEntity !== 'responses');
  const { data: automations } = getCurrentQueryState('fetchAutomations', { orgId, urlParams: { tenantScope: conversationId } }, predefinedEntity !== 'automations');
  const { favoriteAutomations } = useSelector((state) => state.livechat);
  const [responsesSearchText, setResponsesSearchText] = useState();
  const [searchedResponses, setSearchedResponses] = useState();
  const groupedEntities = useMemo(() => generateGroupedEntities(predefinedEntities), [predefinedEntities]);

  useEffect(() => {
    if (predefinedResponses || automations) {
      setSelectedEntity(null);

      if (predefinedEntity === 'automations') {
        setPredefinedEntities(automations);
      }
      if (predefinedEntity === 'responses') {
        setPredefinedEntities(predefinedResponses);
      }
    }
  }, [predefinedResponses, automations, predefinedEntity]);

  useEffect(() => {
    if (favoriteAutomations && automations) {
      const orgAutomations = favoriteAutomations.find(automationGroup => automationGroup.orgId === orgId)?.automations ?? [];
      setOrganizationFavoriteAutomations(automations.filter(automation => orgAutomations.find(item => item.id === automation.id)));
    }
  }, [favoriteAutomations, automations]);

  const triggerResponse = (response) => {
    onEntityTrigger(response);
  };

  const triggerAutomation = (automation) => {
    onEntityTrigger(automation);
  };

  useEffect(() => {
    // TODO: Handle this in a better way?
    if (selectedEntity && isEditMode) {
      const selectedButtonElement = selectedEntity.id
        ? document.querySelector(`[data-key="${selectedEntity.id}"]`)
        : document.querySelector('#addNewResponse');
      selectedButtonElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedEntity]);

  useEffect(() => {
    if (responsesSearchText && responsesSearchText !== '') {
      setSearchedResponses(predefinedResponses?.filter(response => response.text.toLowerCase().includes(responsesSearchText.toLowerCase())));
    } else {
      setSearchedResponses(predefinedResponses);
    }
  }, [responsesSearchText]);

  const markHighlightedWord = (text) => {
    const letters = text.split('');

    return (
      <Typography.Text className="text-muted fw-400">
        {
          letters.map((letter, index) => {
            const isHighlighted = responsesSearchText?.toLowerCase().includes(letter.toLowerCase());
            return isHighlighted ? (
              <Typography.Text key={index} strong>
                {letter}
              </Typography.Text>
            ) : (
              <Fragment key={index}>{letter}</Fragment>
            );
          })
        }
      </Typography.Text>
    );
  };

  if (!isEditMode && predefinedEntities && !predefinedEntities.length) {
    return <Typography.Paragraph className="mb-0 py-1 fs-sm text-center fw-500 text-muted">{predefinedEntity === 'responses' ? t('No canned responses found') : t('No automations found')}</Typography.Paragraph>;
  }

  return (
    <Fragment>

      {
        hasSearch && !isEditMode ? (
          <Fragment>
            <Space align="baseline" className="w-100">
              <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => setIsSearchingResponses(false)}/>

              <Input
                className="border-color-primary"
                placeholder={t('Search canned responses')}
                allowClear
                prefix={<SearchOutlined className="text-primary" />}
                onChange={(e) => setResponsesSearchText(e.target.value)}
              />
            </Space>

            <Row className="overflow-y-auto overflow-x-hidden scrollbar-custom mt-3" style={{ maxHeight: '200px' }}>
              {
                responsesSearchText?.length > 0 ? (
                  <Col span={24} className="ml-3">
                    <Typography.Paragraph className="fs-sm text-muted">{t('CANNED RESPONSES: Search Results for')} "{responsesSearchText}"</Typography.Paragraph>
                  </Col>
                ) : null
              }

              {
                searchedResponses?.length > 0 ? searchedResponses?.map((entity) => {
                  return (
                    <Col span={24} className="mb-1 ml-1">
                      <Tooltip
                        zIndex={9999}
                        placement="rightTop"
                        title={() => {
                          return (
                            <Space size={0} direction="vertical">
                              <Typography.Text strong>{entity.name}</Typography.Text>
                              {markHighlightedWord(entity.text)}
                            </Space>
                          );
                        }}
                      >
                        <Button
                          key={entity.id}
                          data-key={entity.id}
                          size="small"
                          type={'link'}
                          icon={entity.organizationWide && <TeamOutlined />}
                          onClick={() => onPredefinedEntityClick(entity.text)}
                        >
                          <Typography.Text
                            ellipsis
                            className="text-inherit fw-400"
                          >
                            {entity.name}
                          </Typography.Text>
                        </Button>
                      </Tooltip>

                      <Typography.Text className="text-muted fw-400 mx-1">
                          -
                      </Typography.Text>

                      <Typography.Text className="text-muted fw-400">
                        {
                          entity.categories && entity.categories.length > 0
                            ? entity.categories?.map((category, index) => category + (entity.categories.length - 1 !== index ? ' / ' : ' - '))
                            : t('Uncategorized') + ' - '
                        }
                      </Typography.Text>

                      <Typography.Text className="text-muted fw-400 ml-1">
                        {entity.text && responsesSearchText && responsesSearchText !== '' ? markHighlightedWord(extractContextAroundWord({ text: entity.text, substring: responsesSearchText })) : entity.text}
                      </Typography.Text>
                    </Col>
                  );
                }) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('No responses found')}
                    className="mx-auto mt-4"
                  />
                )
              }
            </Row>
          </Fragment>
        ) : null
      }

      {
        hasSearch && isEditMode ? (
          <Fragment>
            <Input
              className="mb-2 text-muted"
              placeholder={t('Search response')}
              size="large"
              allowClear
              prefix={<SearchOutlined />}
              onChange={(e) => setResponsesSearchText(e.target.value)}
            />

            {
              responsesSearchText && responsesSearchText !== '' ? (
                <Row className="mt-3">
                  {
                    responsesSearchText?.length > 0 ? (
                      <Col span={24} className="ml-3">
                        <Typography.Paragraph className="fs-sm text-muted">{t('Search Results for')} "{responsesSearchText}"</Typography.Paragraph>
                      </Col>
                    ) : null
                  }

                  {
                    searchedResponses.length > 0 ? searchedResponses?.map((entity) => {
                      return (
                        <Col span={24} className="mb-1 ml-1">
                          <Button
                            key={entity.id}
                            data-key={entity.id}
                            size="small"
                            type={'link'}
                            icon={entity.organizationWide && <TeamOutlined />}
                            onClick={() => setSelectedEntity(entity)}
                          >
                            <Typography.Text
                              ellipsis
                              className="text-inherit fw-400"
                            >
                              {entity.name}
                            </Typography.Text>
                          </Button>

                          <Row className="ml-2 mb-3">
                            <Col span={24} className="text-left">
                              <Typography.Text italic className="fs-sm text-muted fw-400">
                                {
                                  entity.categories && entity.categories.length > 0
                                    ? entity.categories?.map((category, index) => category + (entity.categories.length - 1 !== index ? ' / ' : ''))
                                    : t('Uncategorized')
                                }
                              </Typography.Text>
                            </Col>

                            <Col span={24} className="text-left mt-1">
                              <Typography.Text className="text-muted fw-400">
                                {entity.text && responsesSearchText && responsesSearchText !== '' ? markHighlightedWord(extractContextAroundWord({ text: entity.text, substring: responsesSearchText })) : entity.text}
                              </Typography.Text>
                            </Col>
                          </Row>
                        </Col>
                      );
                    }) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t('No responses found')}
                        className="mx-auto mt-10"
                      />
                    )
                  }
                </Row>
              ) : null
            }
          </Fragment>
        ) : null
      }

      {
        isEditMode && !responsesSearchText ? (
          <Button
            type="link"
            size="small"
            className="align-self-start px-0 mb-2"
            icon={<PlusCircleFilled />}
            onClick={() => setSelectedEntity({})}
          >
            {t('Create new')}
          </Button>
        ) : null
      }

      {
        predefinedEntities && ((isEditMode && !responsesSearchText) || (!isEditMode && !hasSearch)) ? (
          <Collapse
            ghost
            className="ant-collapse-p-0"
            defaultActiveKey={'Uncategorized'}
            expandIcon={({ isActive }) => <ArrowRightIcon rotate={isActive ? 90 : 0} className="text-secondary mr-2" />}
          >
            {
              Object.keys(groupedEntities).map(category => (
                <Collapse.Panel
                  header={<Typography.Text type="secondary">{category}</Typography.Text>}
                  key={category}
                  className="mb-2"
                >
                  <Space
                    wrap={true}
                    size={4}
                    className="mt-1"
                  >
                    {
                      groupedEntities[category].map(entity => (
                        <Space
                          key={entity.id}
                          wrap={false}
                          align="start"
                          size={0}
                        >
                          <Button
                            key={entity.id}
                            data-key={entity.id}
                            size="small"
                            type={selectedEntity?.id === entity.id ? 'link' : 'text'}
                            className={classNames('mb-1 shadow-soft', {
                              'automation-button': predefinedEntity === 'automations'
                            })}
                            style={{ maxWidth: 280 }}
                            icon={entity.organizationWide && <TeamOutlined />}
                            onClick={() => {
                              if (isEditMode) {
                                setSelectedEntity(entity);
                              } else {
                                if (predefinedEntity === 'automations') {
                                  onPredefinedEntityClick(entity);
                                }
                                if (predefinedEntity === 'responses') {
                                  onPredefinedEntityClick(entity.text);
                                }
                              }
                            }}
                          >
                            <Typography.Text
                              ellipsis
                              className="text-inherit fw-400"
                            >
                              {entity.name}
                            </Typography.Text>
                          </Button>

                          {
                            predefinedEntity === 'automations' && isEditMode && (
                              <Button
                                type="link"
                                size="small"
                                icon={organizationFavoriteAutomations.find(favorite => favorite.id === entity.id) ? <StarFilled className="text-warning fs-sm" /> : <StarOutlined className="text-secondary fs-sm" />}
                                onClick={() => {
                                  const favorites = JSON.parse(window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_FAVORITE_AUTOMATIONS)) || [];
                                  const orgFavorites = favorites.find(automationGroup => automationGroup.orgId === orgId)?.automations ?? [];
                                  const favoritesToUpdate = [
                                    {
                                      orgId,
                                      automations: orgFavorites.find(item => item.id === entity.id)
                                        ? orgFavorites.filter(item => item.id !== entity.id )
                                        : uniqueBy([{ id: entity.id, name: entity.name }, ...orgFavorites], 'id')
                                    },
                                    ...favorites
                                  ];
                                  dispatch(updateFavoriteAutomations(favoritesToUpdate));
                                }}
                              />
                            )
                          }
                        </Space>
                      ))
                    }
                  </Space>
                </Collapse.Panel>
              ))
            }
          </Collapse>
        ) : null
      }

      {
        !predefinedEntities ? (
          <Row align="middle" justify="center">
            <Spin />
          </Row>
        ) : null
      }

      {
        (isEditMode || hasSearch) && selectedEntity && predefinedEntity === 'responses' ? (
          <PredefinedResponsesSidebarForm
            onTriggerResponse={triggerResponse}
            selectedResponse={selectedEntity}
            setSelectedResponse={setSelectedEntity}
          />
        ) : null
      }

      {
        isEditMode && selectedEntity && predefinedEntity === 'automations'? (
          <AutomationsSidebarForm
            onTriggerAutomation={triggerAutomation}
            selectedAutomation={selectedEntity}
            setSelectedAutomation={setSelectedEntity}
          />
        ) : null
      }
    </Fragment>
  );
};

PredefinedEntitiesPanel.propTypes = {
  onPredefinedEntityClick: PropTypes.func,
  isInactive: PropTypes.bool,
  hasSearch: PropTypes.bool,
  isEditMode: PropTypes.bool,
  conversationId: PropTypes.string,
  predefinedEntity: PropTypes.string,
  onEntityTrigger: PropTypes.func,
  setIsSearchingResponses: PropTypes.func
};

export default PredefinedEntitiesPanel;
